import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import CountUp from "react-countup";
import JobZImage from "../../../common/jobz-img";
import { useAuth } from "../../../../context/AuthContext";
import { jobService } from "../../../../services/job-service";
import { supportService } from "../../../../services/support-service";
import { empRoute, employer, publicUser } from "../../../../globals/route-names";
import { motion, MotionPanel, MotionRow, dashboardItem } from "../../../common/dashboard-motion";

function EmpDashboardPage() {
    const { user, updateProfile } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [profileForm, setProfileForm] = useState({ name: user?.name || "", phone: user?.phone || "", address: user?.address || "" });
    const [savingProfile, setSavingProfile] = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([jobService.employerJobs(), jobService.employerApplications(), supportService.myTickets()])
            .then(([jobData, applicationData, ticketData]) => {
                setJobs(jobData.jobs || []);
                setApplications(applicationData.applications || []);
                setTickets(ticketData.tickets || []);
            })
            .catch((error) => setMessage(error.message))
            .finally(() => setLoading(false));
    }, []);

    const stats = useMemo(() => ({
        postedJobs: jobs.length,
        applications: applications.length,
        activeJobs: jobs.filter((job) => job.status === "published").length,
        openTickets: tickets.filter((ticket) => !["resolved", "closed"].includes(ticket.status)).length
    }), [jobs, applications, tickets]);

    const recentApplications = applications.slice(0, 6);
    const profileIncomplete = !user?.name || !user?.phone || !user?.address;

    const updateProfileField = (event) => setProfileForm({ ...profileForm, [event.target.name]: event.target.value });

    const saveProfile = async (event) => {
        event.preventDefault();
        setSavingProfile(true);
        setMessage("");
        try {
            await updateProfile(profileForm);
            setMessage("Employer profile updated. These contact details will be shown on your job pages.");
        } catch (error) {
            setMessage(error.message);
        } finally {
            setSavingProfile(false);
        }
    };

    return (
        <MotionRow className="zz-dashboard-page employer-dashboard-surface">
            <motion.div className="wt-admin-right-page-header clearfix zz-dashboard-hero" variants={dashboardItem}>
                <div className="zz-dashboard-title-row">
                    <div>
                        <h2>Hello, {user?.name || "Employer"}</h2>
                        <div className="breadcrumbs"><NavLink to={publicUser.HOME1}>Home</NavLink><span>Dashboard</span></div>
                    </div>
                    <div className="zz-action-strip">
                        <NavLink className="site-button zz-primary-action" to={empRoute(employer.POST_A_JOB)}><i className="fa fa-plus" /> Add New Job</NavLink>
                        <NavLink className="site-button-secondry" to={empRoute(employer.MANAGE_JOBS)}><i className="fa fa-briefcase" /> Manage Jobs</NavLink>
                        <NavLink className="site-button-secondry" to={empRoute(employer.CANDIDATES)}><i className="fa fa-users" /> View Candidates</NavLink>
                    </div>
                </div>
            </motion.div>
            {message && <div className="alert alert-info">{message}</div>}
            {profileIncomplete && <EmployerProfilePrompt form={profileForm} onChange={updateProfileField} onSubmit={saveProfile} saving={savingProfile} />}
            <motion.div className="twm-dash-b-blocks mb-5" variants={dashboardItem}>
                <div className="row">
                    <StatCard icon="far fa-address-book" className="gradi-1" value={stats.postedJobs} label="Posted Jobs" />
                    <StatCard icon="far fa-file-alt" className="gradi-2" value={stats.applications} label="Total Applications" />
                    <StatCard icon="far fa-check-circle" className="gradi-3" value={stats.activeJobs} label="Active Jobs" />
                    <StatCard icon="far fa-life-ring" className="gradi-4" value={stats.openTickets} label="Open Support Tickets" />
                </div>
            </motion.div>
            <SupportHighlight to={empRoute(employer.SUPPORT)} />
            <div className="twm-pro-view-chart-wrap">
                <div className="row">
                    <div className="col-lg-12 col-md-12 mb-4">
                        <MotionPanel className="panel panel-default site-bg-white m-t30">
                            <div className="panel-heading wt-panel-heading p-a20">
                                <h4 className="panel-tittle m-a0"><i className="far fa-list-alt" />Recent Activities</h4>
                            </div>
                            <div className="panel-body wt-panel-body">
                                <div className="dashboard-list-box list-box-with-icon">
                                    {loading ? (
                                        <div className="p-a20 text-center">Loading dashboard...</div>
                                    ) : (
                                        <ul>
                                            {jobs.slice(0, 4).map((job) => (
                                                <li key={job.id}>
                                                    <i className="fa fa-suitcase text-primary list-box-icon" />
                                                    Your job <a href={`/job-detail/${job.id}`} className="text-primary">{job.title}</a> has {job.application_count || 0} application(s).
                                                </li>
                                            ))}
                                            {applications.slice(0, 4).map((application) => (
                                                <li key={application.id}>
                                                    <i className="fa fa-paperclip text-success list-box-icon" />
                                                    {application.candidate_name} applied for <a href={`/job-detail/${application.job_id}`} className="text-success">{application.job_title}</a>.
                                                </li>
                                            ))}
                                            {jobs.length === 0 && applications.length === 0 && (
                                                <li><i className="fa fa-info text-info list-box-icon" />Post your first job to start receiving candidate enquiries.</li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </MotionPanel>
                    </div>
                    <div className="col-lg-12 col-md-12 mb-4">
                        <MotionPanel className="panel panel-default">
                            <div className="panel-heading wt-panel-heading p-a20">
                                <h4 className="panel-tittle m-a0">Recent Applicants</h4>
                            </div>
                            <div className="panel-body wt-panel-body bg-white">
                                {loading ? (
                                    <div className="p-a20 text-center">Loading applicants...</div>
                                ) : recentApplications.length === 0 ? (
                                    <div className="p-a20 text-center">No applicants yet.</div>
                                ) : (
                                    <div className="twm-dashboard-candidates-wrap">
                                        <div className="row">
                                            {recentApplications.map((application, index) => (
                                                <div className="col-xl-6 col-lg-12 col-md-12" key={application.id}>
                                                    <div className="twm-dash-candidates-list">
                                                        <div className="twm-media">
                                                            <div className="twm-media-pic">
                                                                <JobZImage src={`images/candidates/pic${(index % 6) + 1}.jpg`} alt="#" />
                                                            </div>
                                                        </div>
                                                        <div className="twm-mid-content">
                                                            <h4>{application.candidate_name}</h4>
                                                            <p>{application.job_title}</p>
                                                            <div className="twm-fot-content">
                                                                <div className="twm-left-info">
                                                                    <p className="twm-candidate-address"><i className="feather-map-pin" />{application.job_location || "Location not provided"}</p>
                                                                    <div>{application.candidate_email}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </MotionPanel>
                    </div>
                </div>
            </div>
        </MotionRow>
    )
}

function EmployerProfilePrompt({ form, onChange, onSubmit, saving }) {
    return (
        <MotionPanel className="panel panel-default site-bg-white m-b30 border border-warning">
            <div className="panel-heading wt-panel-heading p-a20">
                <h4 className="panel-tittle m-a0"><i className="fa fa-user-check text-warning" /> Complete Employer Contact Profile First</h4>
            </div>
            <div className="panel-body wt-panel-body p-a20">
                <p>Update employer name, address, and phone number. These employer contact details are shown on all job detail pages instead of shared company contact details.</p>
                <form onSubmit={onSubmit}>
                    <div className="row">
                        <ProfileField label="Employer Name" name="name" value={form.name} onChange={onChange} required />
                        <ProfileField label="Employer Phone Number" name="phone" value={form.phone} onChange={onChange} required />
                        <ProfileField label="Employer Address" name="address" value={form.address} onChange={onChange} required />
                        <div className="col-md-12">
                            <button type="submit" className="site-button" disabled={saving}>{saving ? "Saving..." : "Save Employer Profile"}</button>
                        </div>
                    </div>
                </form>
            </div>
        </MotionPanel>
    );
}

function ProfileField({ label, name, value, onChange, required = false }) {
    return (
        <div className="col-xl-4 col-lg-6 col-md-12">
            <div className="form-group">
                <label>{label}</label>
                <input className="form-control" name={name} value={value || ""} onChange={onChange} required={required} placeholder={label} />
            </div>
        </div>
    );
}

function SupportHighlight({ to }) {
    return (
        <MotionPanel className="panel panel-default site-bg-white m-b30 border border-primary zz-support-panel">
            <div className="panel-body wt-panel-body p-a25 d-flex justify-content-between align-items-center flex-wrap">
                <div>
                    <h4 className="m-b5"><i className="fa fa-headset text-primary" /> Admin Support Tickets</h4>
                    <p className="m-b0">Raise a support ticket for company detail changes, company approval questions, job posting issues, billing, or any portal query.</p>
                </div>
                <NavLink className="site-button" to={to}>Raise Support Ticket</NavLink>
            </div>
        </MotionPanel>
    );
}

function StatCard({ icon, className, value, label }) {
    return (
        <div className="col-xl-3 col-lg-6 col-md-12 mb-3">
            <motion.div className="panel panel-default zz-stat-card" variants={dashboardItem} whileHover={{ y: -5, scale: 1.01 }}>
                <div className={`panel-body wt-panel-body ${className} dashboard-card`}>
                    <div className="wt-card-wrap">
                        <div className="wt-card-icon"><i className={icon} /></div>
                        <div className="wt-card-right counter">
                            <CountUp end={value} duration={1} />
                        </div>
                        <div className="wt-card-bottom">
                            <h4 className="m-b0">{label}</h4>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

export default EmpDashboardPage;
